import { gradeLesson, type GradeLessonOpts } from "./engine";
import type { BlockRegistry } from "./registry";
import type { AuthoredBlock, BlockResponse, LessonResult } from "./types";

/**
 * Progress anti-fraud. Golden rule: the grade that counts is what
 * the server RECALCULATES with authoritative content — never what client
 * sends. This module re-grades and detects manipulation signals.
 */

export interface AttemptSubmission {
  lessonId: string;
  /** Grade CLAIMED by client (ignored except to detect discrepancy). */
  claimedScore?: number;
  responses: BlockResponse[];
  /** Attempt start and end timestamps (epoch ms). */
  startedAt?: number;
  finishedAt?: number;
}

export interface AntifraudOpts extends GradeLessonOpts {
  /** Plausible minimum ms per answered block (default 400). */
  minMsPerBlock?: number;
  /** Client/server discrepancy tolerance before flagging (default 0.001). */
  scoreTolerance?: number;
  /** Attempt IDs already seen (anti-replay). */
  seenAttemptIds?: ReadonlySet<string>;
  /** ID of this attempt (for anti-replay). */
  attemptId?: string;
}

export type FraudFlag =
  | "score-mismatch" // client claimed a different score than recalculated
  | "too-fast" // answered below plausible minimum
  | "duplicate-response" // two answers for same block
  | "unknown-block" // answer to a block that does not exist in lesson
  | "replay"; // intento ya procesado

export interface AntifraudResult {
  /** AUTHORITATIVE grade recalculated on server. The only one persisted. */
  result: LessonResult;
  flags: FraudFlag[];
  /** Accept attempt? (severe flags ⇒ false). */
  accepted: boolean;
}

const GRAVE: ReadonlySet<FraudFlag> = new Set<FraudFlag>(["replay"]);

/**
 * Verifica y RE-CORRIGE un intento. Determinista; ejecutable en el servidor.
 */
export function verifyAttempt(
  registry: BlockRegistry,
  blocks: readonly AuthoredBlock[],
  submission: AttemptSubmission,
  opts: AntifraudOpts = {},
): AntifraudResult {
  const flags: FraudFlag[] = [];
  const minMs = opts.minMsPerBlock ?? 400;
  const tol = opts.scoreTolerance ?? 0.001;

  // Anti-replay.
  if (opts.attemptId && opts.seenAttemptIds?.has(opts.attemptId)) {
    flags.push("replay");
  }

  // Respuestas duplicadas / a bloques inexistentes.
  const blockIds = new Set(blocks.map((b) => b.id));
  const seenResponses = new Set<string>();
  for (const r of submission.responses) {
    if (!blockIds.has(r.blockId)) {
      flags.push("unknown-block");
    }
    if (seenResponses.has(r.blockId)) {
      flags.push("duplicate-response");
    }
    seenResponses.add(r.blockId);
    if (typeof r.elapsedMs === "number" && r.elapsedMs >= 0 && r.elapsedMs < minMs) {
      if (!flags.includes("too-fast")) {
        flags.push("too-fast");
      }
    }
  }

  // Overall timing: if total attempt is faster than minMs × num blocks.
  if (
    submission.startedAt != null &&
    submission.finishedAt != null &&
    submission.finishedAt > submission.startedAt
  ) {
    const elapsed = submission.finishedAt - submission.startedAt;
    if (elapsed < minMs * blocks.length && !flags.includes("too-fast")) {
      flags.push("too-fast");
    }
  }

  // Authoritative re-grading.
  const result = gradeLesson(registry, blocks, submission.responses, opts);

  // Discrepancy with what the client claimed.
  if (submission.claimedScore != null && Math.abs(submission.claimedScore - result.score) > tol) {
    flags.push("score-mismatch");
  }

  const accepted = !flags.some((f) => GRAVE.has(f));
  return { result, flags, accepted };
}
